import { createSelector } from '@reduxjs/toolkit';
import { produce } from 'immer';
import {
  makeSelectAuthApiConfig,
  makeSelectResourceLoadingState,
} from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { AppState, ReduxSelector } from 'store/types';
import {
  bulkEditMessageInfosEndpoint,
  getAmountOfMessagesEndpoint,
  getMessageListEndpoint,
} from './inbox.config';
import { getBulkEditMessageInfoResourceId } from './inbox.helpers';
import { InboxMessage, ModifyMessageInfo } from './inbox.types';

/**
 * Selects the Inbox state.
 * @param reduxState
 */
export function selectInboxState(reduxState: AppState) {
  return reduxState.inbox;
}

/**
 * Selects an object that maps all inbox message by their 'idMessage'.
 * Do not use directly, use 'selectInboxMessages' instead.
 * @param reduxState
 */
export function selectInboxMessagesFromApi(
  reduxState: AppState,
): Record<string, InboxMessage> {
  return selectInboxState(reduxState).inboxMessages;
}

/**
 * Selects the frontend patches of the inbox messages not flushed to the backend.
 * @param reduxState
 */
export function selectOptimisticInboxPatches(reduxState: AppState) {
  return selectInboxState(reduxState).optimisticInboxMessagesPatches;
}

export const selectListOfOptimisticPatches: ReduxSelector<ModifyMessageInfo[]> =
  createSelector(selectOptimisticInboxPatches, (patches) =>
    Object.values(patches),
  );

export const selectInboxMessages: ReduxSelector<Record<string, InboxMessage>> =
  createSelector(
    selectInboxMessagesFromApi,
    selectOptimisticInboxPatches,
    (
      messagesFromApi: Record<string, InboxMessage>,
      optimisticPatches: Record<string, ModifyMessageInfo>,
    ) =>
      produce(messagesFromApi, (draft) => {
        const listOfoptimisticPatches = Object.values(optimisticPatches);

        for (let i = 0, len = listOfoptimisticPatches.length; i < len; i++) {
          const { deleteFlag, idMessage, visionFlag, readFlag } =
            listOfoptimisticPatches[i];

          if (visionFlag) {
            draft[idMessage].visionFlag = 1;
          }

          if (readFlag) {
            draft[idMessage].readFlag = 1;
          }

          if (deleteFlag) {
            delete draft[idMessage];
          }
        }
      }),
  );

/**
 * Outputs the api config to request brief statistics about the user inbox.
 * GET '/inbox-ms/ms/inbox/getNumberMessage'
 * @param reduxState
 */
export function selectGetAmountOfMessagesApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  const requestConfig = makeSelectAuthApiConfig({
    url: getAmountOfMessagesEndpoint,
    method: 'GET',
  })(reduxState);

  return {
    resourceId: getAmountOfMessagesEndpoint,
    requestConfig,
    invalidateIfPresent: true,
    skipDefaultServerErrorHandling: true,
  };
}

export const selectGetAmountOfMessagesLoadState =
  makeSelectResourceLoadingState(getAmountOfMessagesEndpoint);

export function selectInboxStats(reduxState: AppState) {
  return selectInboxState(reduxState).summaryInboxInfos;
}

export function selectInboxErrors(reduxState: AppState) {
  return selectInboxState(reduxState).errors;
}

/**
 * Creates the api config to query all the user inbox messages.
 * GET '/inbox-ms/ms/inbox/getMessageList'
 * @param reduxState
 */
export function selectGetAllSgagInboxMessagesApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  const requestConfig = makeSelectAuthApiConfig({
    method: 'GET',
    url: getMessageListEndpoint,
    // Params extracted from 'https://mtest-liferay.sisal.it/'.
    params: {
      category: 4,
      idFamilyList: '',
    },
  })(reduxState);

  return {
    requestConfig,
    resourceId: getMessageListEndpoint,
    invalidateIfPresent: true,
    skipDefaultServerErrorHandling: true,
  };
}

export const selectGetAllSgagInboxMessagesLoadState =
  makeSelectResourceLoadingState(getMessageListEndpoint);

/**
 * Selects the array of all the messages in no particular order.
 */
export const selectAllMessagesList: ReduxSelector<InboxMessage[]> =
  createSelector(selectInboxMessages, (inboxMessages) =>
    Object.values(inboxMessages),
  );

/**
 * Returns 'true' if we are performing an UI blocking update to the inbox.
 * @param reduxState
 */
export function selectIsInboxUpdating(reduxState: AppState): boolean {
  return selectInboxState(reduxState).isLoading;
}

/**
 * Creates a selector that returns a apiConfig to flush
 * the modifications provided to the backend.
 * @param patches @see ModifyMessageInfo
 */
export function makeSelectApplyMessagePatchesApiConfig(
  patches: ModifyMessageInfo[],
) {
  return function selectApplyMessagePatchesApiConfig(reduxState: AppState) {
    const allMessagesFromApi = selectInboxMessagesFromApi(reduxState);

    const patchesWithAllValues: Required<
      Omit<ModifyMessageInfo, 'isConfirmedRATD'>
    >[] = patches.map((patch) => {
      const messageFromApi = allMessagesFromApi[patch.idMessage];

      return {
        idMessage: patch.idMessage,
        readFlag: patch.readFlag ?? messageFromApi.readFlag,
        visionFlag: patch.visionFlag ?? messageFromApi.visionFlag,
        deleteFlag: patch.deleteFlag ?? 0,
      };
    });

    const requestConfig = makeSelectAuthApiConfig({
      url: bulkEditMessageInfosEndpoint,
      method: 'POST',
      data: {
        infoMessages: patchesWithAllValues,
      },
    })(reduxState);

    return {
      requestConfig,
      resourceId: getBulkEditMessageInfoResourceId(patchesWithAllValues),
    };
  };
}

/**
 * Creates a selector that returns the api config to bulk mark the messages as
 * @param messages An array of messages.
 * @param bulkModifications @see ModifyMessageInfo
 */
export function makeSelectBulkEditMessageInfosApiConfig(
  messages: InboxMessage[],
  bulkModifications: Omit<ModifyMessageInfo, 'idMessage'>,
) {
  const modifyMessage = (message: InboxMessage): ModifyMessageInfo => ({
    ...bulkModifications,
    idMessage: message.idMessage,
  });

  return makeSelectApplyMessagePatchesApiConfig(messages.map(modifyMessage));
}

/**
 * Creates a selector that returns the api config to bulk **remove all** the
 * input messages passed.
 * @param messages An array of messages
 */
export function makeSelectDeleteMessagesApiConfig(messages: InboxMessage[]) {
  const modifyMessageInfo: Omit<ModifyMessageInfo, 'idMessage'> = {
    deleteFlag: 1,
  };

  return makeSelectBulkEditMessageInfosApiConfig(messages, modifyMessageInfo);
}
